import React from 'react';
import { getStreakLevel, getNextMilestone } from '../services/streakService';

const StreakMilestoneModal = ({ 
  isOpen, 
  onClose, 
  streakData, 
  isNewStreak = false,
  wasStreakBroken = false 
}) => {
  if (!isOpen || !streakData) return null;

  const currentLevel = getStreakLevel(streakData.currentStreak);
  const nextMilestone = getNextMilestone(streakData.currentStreak);
  
  // Determine the type of visit
  const getVisitType = () => {
    if (wasStreakBroken) return 'streak_broken';
    if (isNewStreak || streakData.currentStreak === 1) return 'first_visit';
    return 'streak_continued';
  };

  const visitType = getVisitType();

  const getModalContent = () => {
    switch (visitType) {
      case 'streak_broken':
        return {
          title: 'Streak Reset',
          subtitle: `Back to day 1, but every restart is a new beginning!`,
          icon: '🔄',
          message: 'Your streak was reset, but you\'re back! Keep the momentum going.',
          buttonText: 'Let\'s Go!'
        };
      
      case 'first_visit':
        return {
          title: 'Welcome Back!',
          subtitle: `Day ${streakData.currentStreak} • ${currentLevel?.name || 'Beginner'} ${currentLevel?.icon || '🌱'}`,
          icon: '🎯',
          message: streakData.currentStreak === 1 
            ? 'You\'re starting your debate journey! Come back tomorrow to build your streak.'
            : `You're on a ${streakData.currentStreak}-day streak! Keep the momentum going.`,
          buttonText: 'Continue Debating'
        };
      
      default:
        return {
          title: 'Streak Continued!',
          subtitle: `Day ${streakData.currentStreak} • ${currentLevel?.name || 'Beginner'} ${currentLevel?.icon || '🌱'}`,
          icon: '🔥',
          message: `Amazing! You're on a ${streakData.currentStreak}-day streak. ${currentLevel?.description || ''}`,
          buttonText: 'Keep Going!'
        };
    }
  };

  const content = getModalContent();

  // Calculate flame intensity for animation
  const flameIntensity = Math.min(streakData.currentStreak / 30, 1);

  return (
    <div className="streak-milestone-modal-overlay" onClick={onClose}>
      <div className="streak-milestone-modal" onClick={(e) => e.stopPropagation()}>
        <div className="streak-modal-header">
          <div className="streak-modal-icon">{content.icon}</div>
          <h2 className="streak-modal-title">{content.title}</h2>
          <p className="streak-modal-subtitle">{content.subtitle}</p>
        </div>

        <div className="streak-modal-body">
          <div className="streak-visual-container">
            {/* Animated flame based on streak level */}
            <div 
              className={`streak-flame streak-level-${Math.min(Math.floor(streakData.currentStreak / 7) + 1, 5)}`}
              style={{ '--flame-intensity': flameIntensity }}
            >
              {currentLevel?.icon || '🌱'}
            </div>
            
            <div className="streak-counter">
              <span className="streak-number">{streakData.currentStreak}</span>
              <span className="streak-label">day{streakData.currentStreak !== 1 ? 's' : ''}</span>
            </div>
          </div>

          <p className="streak-modal-message">{content.message}</p>

          {/* Progress to next milestone */}
          {nextMilestone && (
            <div className="next-milestone-preview">
              <div className="milestone-progress-bar">
                <div 
                  className="milestone-progress-fill"
                  style={{ 
                    width: `${(streakData.currentStreak / nextMilestone.days) * 100}%` 
                  }}
                ></div>
              </div>
              <p className="next-milestone-text">
                {nextMilestone.daysLeft} more day{nextMilestone.daysLeft !== 1 ? 's' : ''} until{' '}
                <strong>{nextMilestone?.level?.name || 'Next Level'}</strong> {nextMilestone?.level?.icon || '🎯'}
              </p>
            </div>
          )}

          {/* Show longest streak if impressive */}
          {streakData.longestStreak > 3 && streakData.longestStreak > streakData.currentStreak && (
            <div className="longest-streak-info">
              <span className="longest-streak-label">Personal Best:</span>
              <span className="longest-streak-value">{streakData.longestStreak} days</span>
            </div>
          )}
        </div>

        <div className="streak-modal-footer">
          <button 
            className="streak-modal-button" 
            onClick={onClose}
          >
            {content.buttonText}
          </button>
        </div>
      </div>
    </div>
  );
};

export default StreakMilestoneModal;